import { Controller } from "@hotwired/stimulus"
import { Modal } from "flowbite"

// Connects to data-controller="uploader-modal"
export default class extends Controller {
  static targets = ["previews", "previewTemplate", "confirm", "error"]

  static SINGLE_PREVIEW_LAYOUT = "flex flex-col items-center overflow-auto"
  static MULTIPLE_PREVIEWS_LAYOUT = "grid grid-cols-1 sm:grid-cols-2 gap-16 overflow-auto"
  static SINGLE_MEDIA_CLASSES = "size-305 object-cover border border-seasalt-300 rounded-2xl"
  static MULTIPLE_MEDIA_CLASSES = "w-full aspect-square object-cover border border-seasalt-300 rounded-2xl"
  static SINGLE_VIDEO_CLASSES = "size-305 object-cover rounded-2xl"
  static MULTIPLE_VIDEO_CLASSES = "w-full aspect-square object-cover rounded-2xl"

  connect() {
    this.handleOpen = this.handleOpen.bind(this)
    this.handleClose = this.handleClose.bind(this)
    this.handlePreviewsChanged = this.handlePreviewsChanged.bind(this)
    this.handleFlagsChanged = this.handleFlagsChanged.bind(this)

    this.modal = new Modal(this.element, {
      backdropClasses: "bg-transparent fixed inset-0 z-52",
      onHide: () => this.emit("uploader-modal:cancel")
    })

    document.addEventListener("uploader:open", this.handleOpen)
    document.addEventListener("uploader:close", this.handleClose)
    document.addEventListener("uploader:previews-changed", this.handlePreviewsChanged)
    document.addEventListener("uploader:flags-changed", this.handleFlagsChanged)
  }

  disconnect() {
    document.removeEventListener("uploader:open", this.handleOpen)
    document.removeEventListener("uploader:close", this.handleClose)
    document.removeEventListener("uploader:previews-changed", this.handlePreviewsChanged)
    document.removeEventListener("uploader:flags-changed", this.handleFlagsChanged)

    this.modal.destroyAndRemoveInstance()
  }

  confirm() {
    this.emit("uploader-modal:confirm")
  }

  addMore() {
    this.emit("uploader-modal:add-more")
  }

  cancel() {
    this.modal.hide()
  }

  remove(event) {
    this.emit("uploader-modal:remove-file", { index: this.indexOf(event.currentTarget) })
  }

  captionChanged(event) {
    this.emit("uploader-modal:caption-changed", {
      index: this.indexOf(event.currentTarget),
      caption: event.currentTarget.value
    })
  }

  handleOpen() {
    this.modal.show()
  }

  handleClose() {
    this.modal.hide()
  }

  handlePreviewsChanged(event) {
    this.renderPreviews(event.detail.files)
  }

  handleFlagsChanged(event) {
    this.applyFlags(event.detail)
  }

  renderPreviews(files) {
    this.previewsTarget.replaceChildren()
    this.previewsTarget.className = files.length > 1
      ? this.constructor.MULTIPLE_PREVIEWS_LAYOUT
      : this.constructor.SINGLE_PREVIEW_LAYOUT

    files.forEach((file, index) => {
      this.previewsTarget.appendChild(this.buildPreview(file, index, files.length))
    })
  }

  buildPreview(file, index, totalFiles) {
    const preview = this.previewTemplateTarget.content.cloneNode(true)

    preview.querySelector("[data-media-slot]").replaceWith(this.buildMedia(file, totalFiles))
    preview.querySelector("button").dataset.index = index

    const caption = preview.querySelector("textarea")
    caption.dataset.index = index
    caption.textContent = file.caption

    return preview
  }

  buildMedia(file, totalFiles) {
    const media = document.createElement(file.isVideo ? "video" : "img")
    media.src = file.previewUrl
    media.className = this.mediaClassesFor(file, totalFiles)

    if (file.isVideo) {
      media.controls = true
    }

    return media
  }

  mediaClassesFor(file, totalFiles) {
    if (file.isVideo) {
      return totalFiles > 1
        ? this.constructor.MULTIPLE_VIDEO_CLASSES
        : this.constructor.SINGLE_VIDEO_CLASSES
    }

    return totalFiles > 1
      ? this.constructor.MULTIPLE_MEDIA_CLASSES
      : this.constructor.SINGLE_MEDIA_CLASSES
  }

  applyFlags({ errorMessage, canSend }) {
    this.confirmTarget.disabled = !canSend
    this.errorTarget.textContent = errorMessage || ""
    this.errorTarget.classList.toggle("hidden", !errorMessage)
  }

  indexOf(element) {
    return Number(element.dataset.index)
  }

  emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }))
  }
}
